// Läser ändringsloggen för ett dokument: vem som ändrat vad och när.
import { supabase } from "@/integrations/supabase/client";

export interface AuditEntry {
  id: string;
  action: string;
  document_type: string;
  doc_number: string | null;
  record_id: string | null;
  actor_name: string | null;
  created_at: string;
  changes: Record<string, { from: unknown; to: unknown }>;
}

export interface AuditTarget {
  projectId?: string | null;
  /** Utelämnas för projektomfattande logg (alla dokumenttyper) */
  documentType?: string;
  recordId?: string | null;
  title?: string;
}

export interface AuditFilters {
  /** Begränsa till vissa händelser, t.ex. ["deleted","restored","purged"] */
  actions?: string[];
  documentType?: string;
  /** ISO-datum */
  from?: string;
  to?: string;
  /** Fritextsökning på person */
  actor?: string;
}

export const AUDIT_ACTION_LABELS: Record<string, string> = {
  created: "Skapade",
  updated: "Ändrade",
  deleted: "Tog bort",
  restored: "Återställde",
  purged: "Raderade permanent",
  restored_version: "Återställde version",
};

/** Grupper som används i filtret i ändringsloggen. */
export const AUDIT_ACTION_GROUPS: { value: string; label: string; actions: string[] }[] = [
  { value: "all", label: "Alla händelser", actions: [] },
  { value: "created", label: "Skapade", actions: ["created"] },
  { value: "updated", label: "Ändringar", actions: ["updated"] },
  { value: "deleted", label: "Borttagningar", actions: ["deleted"] },
  { value: "restored", label: "Återställningar", actions: ["restored", "restored_version"] },
  { value: "purged", label: "Permanent raderat", actions: ["purged"] },
  {
    value: "deletions",
    label: "Endast raderingar (alla typer)",
    actions: ["deleted", "restored", "purged"],
  },
];

export const AUDIT_DOCUMENT_LABELS: Record<string, string> = {
  risk_assessment: "Risksammanställning",
  skr_assessment: "SKR-bedömning",
  work_preparation: "Arbetsberedning",
  amp_section: "Arbetsmiljöplan",
};

export const AUDIT_FIELD_LABELS: Record<string, string> = {
  status: "Status",
  note: "Kommentar",
  activity: "Aktivitet",
  responsible: "Ansvarig",
  due_date: "Klart senast",
  required: "Obligatorisk",
  valid_from: "Gäller från",
  valid_to: "Gäller till",
  signed_at: "Undertecknat",
  to_name: "Mottagare",
  from_name: "Fördelas av",
  scope: "Omfattning",
  authority: "Befogenheter",
  deputy_name: "Ställföreträdare",
  sections: "Innehåll",
  name: "Namn",
  version: "Version",
  task: "Arbetsuppgift",
  representative: "Representant",
  handler: "Handläggare",
  basp: "Bas-P",
  basu: "Bas-U",
  reminder_ack_at: "Påminnelse kvitterad",
};

export const formatAuditValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "tomt";
  if (typeof value === "boolean") return value ? "Ja" : "Nej";
  if (typeof value === "object") return "uppdaterat innehåll";
  return String(value);
};

export const fetchAuditLog = async (
  target: AuditTarget,
  filters: AuditFilters = {},
): Promise<AuditEntry[]> => {
  let query = supabase
    .from("document_audit_log")
    .select("id, action, document_type, doc_number, record_id, actor_name, created_at, changes")
    .order("created_at", { ascending: false })
    .limit(500);

  const documentType = filters.documentType || target.documentType;
  if (documentType) query = query.eq("document_type", documentType);
  if (target.projectId) query = query.eq("project_id", target.projectId);
  if (target.recordId) query = query.eq("record_id", target.recordId);
  if (filters.actions && filters.actions.length > 0) query = query.in("action", filters.actions);
  if (filters.from) query = query.gte("created_at", filters.from);
  if (filters.to) query = query.lte("created_at", `${filters.to}T23:59:59`);
  if (filters.actor) query = query.ilike("actor_name", `%${filters.actor}%`);

  const { data, error } = await query;
  if (error) return [];
  return (data as unknown as AuditEntry[]) ?? [];
};

/** Gör om träfflistan till CSV så den kan sparas ned. */
export const auditEntriesToCsv = (entries: AuditEntry[]): string => {
  const rows = [
    ["Tidpunkt", "Händelse", "Dokumenttyp", "Dok", "Person", "Ändringar"],
    ...entries.map((e) => [
      new Date(e.created_at).toLocaleString("sv-SE"),
      AUDIT_ACTION_LABELS[e.action] ?? e.action,
      AUDIT_DOCUMENT_LABELS[e.document_type] ?? e.document_type,
      e.doc_number ?? "",
      e.actor_name ?? "",
      Object.entries(e.changes || {})
        .map(
          ([field, diff]) =>
            `${AUDIT_FIELD_LABELS[field] ?? field}: ${formatAuditValue(diff?.from)} → ${formatAuditValue(diff?.to)}`,
        )
        .join(" | "),
    ]),
  ];
  return rows
    .map((cols) => cols.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";"))
    .join("\n");
};
